import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Archive, Download, FileUp, Pencil, Plus, RefreshCw, Upload } from 'lucide-react';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { api, del, get, mensagemDeErro, patch, post, traduzirErro } from '../../nucleo/api';
import { data, dataHora, nomeDeArquivo, nomePessoa, numero } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { campoEmpresaDoRegistro, empresaEscolhida, precisaEscolherEmpresa } from '../../nucleo/empresa/empresa-do-registro';
import { COR_IMPORTACAO, MARCAS, invalidarPonto, type Importacao, type Relogio } from './tipos';

/**
 * Lote 3: relógios de ponto na Configuração do ponto. Cadastro (marca, modelo, número de série, empresa, local), importação do
 * AFD (o arquivo vai pelo `/anexos` do núcleo e o job grava) e, na Control iD, a leitura automática pela API.
 */
export function Relogios() {
  const { t } = useTranslation('ponto');
  const s = useSessao();
  const [aberto, setAberto] = useState<Relogio | 'novo' | null>(null);
  const [ficha, setFicha] = useState<Relogio | null>(null);
  const colunas: Coluna<Relogio>[] = [
    { chave: 'nome', rotulo: t('relogio.campos.nome'), principal: true, render: (r) => <span className="font-medium">{r.nome}</span> },
    { chave: 'marca', rotulo: t('relogio.campos.marca'), render: (r) => [t(`relogio.marca.${r.marca}`), r.modelo].filter(Boolean).join(' ') },
    { chave: 'numero_serie', rotulo: t('relogio.campos.numeroSerie'), mono: true },
    { chave: 'empresa_nome', rotulo: t('relogio.campos.empresa'), render: (r) => r.empresa_nome ?? '-' },
    { chave: 'local', rotulo: t('relogio.campos.local'), render: (r) => r.local ?? '-' },
    { chave: 'leitura_automatica', rotulo: t('relogio.campos.leituraAutomatica'), render: (r) => (r.leitura_automatica ? t('sim') : t('nao')) },
    { chave: 'marcacoes', rotulo: t('relogio.campos.marcacoes'), alinhar: 'direita', mono: true, render: (r) => numero(r.marcacoes) },
    { chave: 'ultima_leitura_em', rotulo: t('relogio.campos.ultimaLeitura'), oculta: true, render: (r) => (r.ultima_leitura_em ? dataHora(r.ultima_leitura_em) : '-') },
    { chave: 'ativo', rotulo: t('relogio.campos.ativo'), render: (r) => <Selo cor={r.ativo ? 'verde' : 'neutro'}>{r.ativo ? t('sim') : t('nao')}</Selo> },
  ];
  if (!s.pode('ponto.ver_todos')) return null;
  return (
    <Cartao
      titulo={t('relogio.titulo')}
      acao={
        s.pode('ponto.configurar') ? (
          <Botao icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
            {t('relogio.novo')}
          </Botao>
        ) : undefined
      }
    >
      <p className="mb-3 text-sm text-tinta-3">{t('relogio.aviso')}</p>
      <Tabela<Relogio> tela="ponto.relogios" caminho="/ponto/relogios" chaveConsulta={['ponto', 'relogios']} colunas={colunas} aoClicar={(r) => setFicha(r)} vazio={<Vazio texto={t('relogio.vazio')} />} />
      {aberto && <FormRelogio relogio={aberto === 'novo' ? null : aberto} aoFechar={() => setAberto(null)} />}
      {ficha && !aberto && <FichaRelogio id={ficha.id} aoFechar={() => setFicha(null)} aoEditar={(r) => setAberto(r)} />}
    </Cartao>
  );
}

function FormRelogio({ relogio: r, aoFechar }: { relogio: Relogio | null; aoFechar: () => void }) {
  const { t } = useTranslation('ponto');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useMemo(() => r?.id ?? crypto.randomUUID(), [r]);
  const empresa = s.empresas?.atual ?? null;
  // Rodada rval1-1 (RVAL1-ROB-M2): sem empresa atual (troca desligada ou "Minhas verticais") e com mais de uma, a empresa do
  // relógio (a do CNPJ do AFD) é escolhida no próprio formulário, como a "Empresa do registro".
  const escolherEmpresa = !r && precisaEscolherEmpresa(s.empresas);
  const campos: DefCampo[] = [
    ...(r ? [] : campoEmpresaDoRegistro(s.empresas, { rotulo: t('relogio.campos.empresa'), ajuda: t('relogio.campos.empresaAjuda') })),
    { nome: 'nome', rotulo: t('relogio.campos.nome'), tipo: 'texto', obrigatorio: true, max: 120 },
    { nome: 'marca', rotulo: t('relogio.campos.marca'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: MARCAS.map((m) => ({ valor: m, rotulo: t(`relogio.marca.${m}`) })) },
    { nome: 'modelo', rotulo: t('relogio.campos.modelo'), tipo: 'texto', largura: 'meia', max: 60 },
    { nome: 'numero_serie', rotulo: t('relogio.campos.numeroSerie'), tipo: 'texto', obrigatorio: true, ajuda: t('relogio.campos.numeroSerieAjuda'), max: 40 },
    { nome: 'local', rotulo: t('relogio.campos.local'), tipo: 'texto', ajuda: t('relogio.campos.localAjuda'), max: 200 },
    { nome: 'fuso', rotulo: t('relogio.campos.fuso'), tipo: 'texto', largura: 'meia', ajuda: t('relogio.campos.fusoAjuda'), max: 60 },
    { nome: 'conferir_crc', rotulo: t('relogio.campos.conferirCrc'), tipo: 'booleano', largura: 'meia', ajuda: t('relogio.campos.conferirCrcAjuda') },
    { nome: 'ativo', rotulo: t('relogio.campos.ativo'), tipo: 'booleano', largura: 'meia' },
    { nome: 'leitura_automatica', rotulo: t('relogio.campos.leituraAutomatica'), tipo: 'booleano', largura: 'meia', ajuda: t('relogio.avisoLeitura'), visivel: (v) => v.marca === 'controlid' },
    { nome: 'endereco', rotulo: t('relogio.campos.endereco'), tipo: 'texto', ajuda: t('relogio.campos.enderecoAjuda'), max: 300, visivel: (v) => v.marca === 'controlid' && !!v.leitura_automatica },
    ...(r?.certificado_sha256 ? [{ nome: 'esquecer_certificado', rotulo: t('relogio.campos.esquecerCertificado'), tipo: 'booleano', ajuda: t('relogio.campos.esquecerCertificadoAjuda') } as DefCampo] : []),
  ];
  const inicial = r
    ? { nome: r.nome, marca: r.marca, modelo: r.modelo ?? '', numero_serie: r.numero_serie, local: r.local ?? '', fuso: r.fuso, conferir_crc: r.conferir_crc, ativo: r.ativo, leitura_automatica: r.leitura_automatica, endereco: r.endereco ?? '' }
    : { marca: 'controlid', fuso: 'America/Sao_Paulo', conferir_crc: true, ativo: true, leitura_automatica: false };
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      titulo={r ? t('relogio.editar') : t('relogio.novo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-relogio-ponto" aria-disabled={!r && !empresa && !escolherEmpresa}>
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      {!r && !empresa && !escolherEmpresa && <p className="mb-3 text-sm text-tinta-3">{t('relogio.semEmpresa')}</p>}
      <Formulario
        id="form-relogio-ponto"
        campos={campos}
        inicial={inicial}
        aoEnviar={async (v) => {
          const automatica = v.marca === 'controlid' && !!v.leitura_automatica;
          const corpo = {
            nome: v.nome,
            marca: v.marca,
            modelo: v.modelo || null,
            numero_serie: v.numero_serie,
            local: v.local || null,
            fuso: v.fuso || 'America/Sao_Paulo',
            conferir_crc: !!v.conferir_crc,
            ativo: !!v.ativo,
            leitura_automatica: automatica,
            endereco: automatica ? v.endereco || null : null,
          };
          if (r) await patch(`/ponto/relogios/${r.id}`, { ...corpo, ...(v.esquecer_certificado ? { esquecer_certificado: true } : {}) });
          else {
            const daEmpresa = empresa ?? empresaEscolhida(v);
            if (!daEmpresa) throw new Error(t('relogio.semEmpresa'));
            await post('/ponto/relogios', { id, empresa_id: daEmpresa, ...corpo });
          }
          await invalidarPonto(qc);
          toast.sucesso(tn('comum.salvo'));
          aoFechar();
        }}
      />
    </Drawer>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex flex-wrap justify-between gap-2 border-b border-borda py-2">
      <dt className="text-tinta-3">{rotulo}</dt>
      <dd className="min-w-0 break-words text-right text-tinta-1">{valor}</dd>
    </div>
  );
}

function FichaRelogio({ id, aoFechar, aoEditar }: { id: string; aoFechar: () => void; aoEditar: (r: Relogio) => void }) {
  const { t } = useTranslation('ponto');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [importando, setImportando] = useState(false);
  const [arquivando, setArquivando] = useState(false);
  const [relatorio, setRelatorio] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['ponto', 'relogios', id], queryFn: () => get<Relogio>(`/ponto/relogios/${id}`) });
  const importacoes = useQuery({
    queryKey: ['ponto', 'importacoes', id],
    queryFn: () => get<{ itens: Importacao[] }>('/ponto/importacoes', { relogio_id: id, limite: 20 }),
    refetchInterval: (c) => (c.state.data?.itens.some((i) => i.estado === 'na_fila' || i.estado === 'processando') ? 3000 : false),
  });
  const ler = useMutation({
    mutationFn: () => post(`/ponto/relogios/${id}/ler`, {}),
    onSuccess: async () => {
      toast.info(t('relogio.leituraNaFila'));
      await invalidarPonto(qc);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const arquivar = useMutation({
    mutationFn: () => del(`/ponto/relogios/${id}`),
    onSuccess: async () => {
      setArquivando(false);
      await invalidarPonto(qc);
      aoFechar();
    },
    onError: (e) => {
      setArquivando(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  const r = q.data;
  return (
    <>
      <Drawer
        aberto={!importando && !relatorio && !arquivando}
        aoFechar={aoFechar}
        largura="lg"
        titulo={r?.nome ?? t('relogio.titulo')}
        rodape={
          r ? (
            <>
              {s.pode('ponto.configurar') && (
                <Botao variante="fantasma" icone={<Archive className="size-4" />} onClick={() => setArquivando(true)}>
                  {t('relogio.arquivar')}
                </Botao>
              )}
              {s.pode('ponto.configurar') && (
                <Botao icone={<Pencil className="size-4" />} onClick={() => aoEditar(r)}>
                  {t('relogio.editar')}
                </Botao>
              )}
              {s.pode('ponto.fechar') && r.leitura_automatica && (
                <Botao icone={<RefreshCw className="size-4" />} carregando={ler.isPending} onClick={() => ler.mutate()}>
                  {t('relogio.lerAgora')}
                </Botao>
              )}
              {s.pode('ponto.fechar') && r.ativo && (
                <Botao variante="primario" icone={<FileUp className="size-4" />} onClick={() => setImportando(true)}>
                  {t('relogio.importar')}
                </Botao>
              )}
            </>
          ) : undefined
        }
      >
        <Estado consulta={q}>
          {(r) => (
            <>
              <dl className="mb-4 text-sm">
                <Linha rotulo={t('relogio.campos.marca')} valor={[t(`relogio.marca.${r.marca}`), r.modelo].filter(Boolean).join(' ')} />
                <Linha rotulo={t('relogio.campos.numeroSerie')} valor={r.numero_serie} />
                <Linha rotulo={t('relogio.campos.empresa')} valor={r.empresa_nome ?? '-'} />
                <Linha rotulo={t('relogio.campos.local')} valor={r.local ?? '-'} />
                <Linha rotulo={t('relogio.campos.fuso')} valor={r.fuso} />
                <Linha rotulo={t('relogio.campos.conferirCrc')} valor={r.conferir_crc ? t('sim') : t('nao')} />
                <Linha rotulo={t('relogio.campos.leituraAutomatica')} valor={r.leitura_automatica ? t('sim') : t('nao')} />
                {r.leitura_automatica && <Linha rotulo={t('relogio.campos.endereco')} valor={r.endereco ?? '-'} />}
                {r.leitura_automatica && <Linha rotulo={t('relogio.campos.certificado')} valor={r.certificado_sha256 ?? '-'} />}
                <Linha rotulo={t('relogio.campos.ultimoNsr')} valor={r.ultimo_nsr === null ? '-' : String(r.ultimo_nsr)} />
                <Linha rotulo={t('relogio.campos.ultimaLeitura')} valor={r.ultima_leitura_em ? dataHora(r.ultima_leitura_em) : '-'} />
                {r.ultimo_erro && <Linha rotulo={t('relogio.campos.ultimoErro')} valor={traduzirErro(r.ultimo_erro_codigo ?? undefined, r.ultimo_erro)} />}
              </dl>
              {r.leitura_automatica && <p className="mb-4 text-sm text-tinta-3">{t('relogio.avisoCertificado')}</p>}
              <h3 className="mb-2 text-sm font-semibold text-tinta-1">{t('relogio.importacoes')}</h3>
              <Estado consulta={importacoes}>
                {(d) =>
                  d.itens.length ? (
                    <ul className="flex flex-col gap-2">
                      {d.itens.map((i) => (
                        <li key={i.id}>
                          <button type="button" onClick={() => setRelatorio(i.id)} className="flex w-full min-w-0 flex-wrap items-center justify-between gap-2 rounded-lg border border-borda p-3 text-left text-sm hover:bg-sup-2">
                            <span className="flex min-w-0 flex-col">
                              <span className="font-medium text-tinta-1">{dataHora(i.created_at)}</span>
                              <span className="text-tinta-3">{t(`relogio.origem.${i.origem}`)}</span>
                            </span>
                            <span className="flex flex-wrap items-center gap-2">
                              {i.estado === 'concluida' && <span className="numero text-tinta-2">{`${t('relogio.campos.gravadas')}: ${numero(i.gravadas)}`}</span>}
                              <Selo cor={COR_IMPORTACAO[i.estado]}>{t(`relogio.estado.${i.estado}`)}</Selo>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Vazio texto={t('relogio.vazioImportacoes')} />
                  )
                }
              </Estado>
            </>
          )}
        </Estado>
      </Drawer>
      {importando && r && <ImportarAfd relogio={r} aoFechar={() => setImportando(false)} />}
      {relatorio && <RelatorioImportacao id={relatorio} aoFechar={() => setRelatorio(null)} />}
      <Confirmar aberto={arquivando} aoFechar={() => setArquivando(false)} aoConfirmar={() => arquivar.mutate()} titulo={t('relogio.confirmarArquivar')} texto={t('relogio.confirmarArquivarTexto')} perigoso carregando={arquivar.isPending} />
    </>
  );
}

function ImportarAfd({ relogio, aoFechar }: { relogio: Relogio; aoFechar: () => void }) {
  const { t } = useTranslation('ponto');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useMemo(() => crypto.randomUUID(), []);
  const seletor = useRef<HTMLInputElement>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const enviar = useMutation({
    mutationFn: async () => {
      if (!arquivo) return;
      const fd = new FormData();
      fd.append('id', crypto.randomUUID());
      fd.append('entidade', 'ponto.importacao');
      fd.append('registro_id', id);
      fd.append('arquivo', arquivo);
      const anexo = await api<{ id: string }>('POST', '/anexos', fd);
      await post(`/ponto/relogios/${relogio.id}/importar`, { id, anexo_id: anexo.id });
    },
    onSuccess: async () => {
      toast.info(t('relogio.naFila'));
      await invalidarPonto(qc);
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={`${t('relogio.importar')} · ${relogio.nome}`}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={enviar.isPending} aria-disabled={!arquivo} onClick={() => arquivo && enviar.mutate()}>
            {t('relogio.importar')}
          </Botao>
        </>
      }
    >
      <p className="mb-4 text-sm text-tinta-3">{t('relogio.avisoImportar')}</p>
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-tinta-2">{t('relogio.arquivo')}</span>
        <div className="relative flex min-w-0 flex-wrap items-center gap-3">
          <Botao icone={<Upload className="size-4" />} onClick={() => seletor.current?.click()}>
            {arquivo ? t('relogio.trocarArquivo') : t('relogio.escolherArquivo')}
          </Botao>
          {arquivo && (
            <span className="min-w-0 truncate text-sm text-tinta-2" title={nomeDeArquivo(arquivo.name)}>
              {nomeDeArquivo(arquivo.name)}
            </span>
          )}
          <input ref={seletor} type="file" accept=".txt,text/plain" className="sr-only" tabIndex={-1} aria-label={t('relogio.arquivo')} title={t('relogio.arquivo')} onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} />
        </div>
      </div>
    </Modal>
  );
}

function RelatorioImportacao({ id, aoFechar }: { id: string; aoFechar: () => void }) {
  const { t } = useTranslation('ponto');
  const q = useQuery({ queryKey: ['ponto', 'importacoes', 'uma', id], queryFn: () => get<Importacao>(`/ponto/importacoes/${id}`) });
  return (
    <Drawer aberto aoFechar={aoFechar} largura="lg" titulo={t('relogio.verRelatorio')}>
      <Estado consulta={q}>
        {(i) => (
          <>
            <dl className="mb-4 text-sm">
              <Linha rotulo={t('relogio.campos.estado')} valor={t(`relogio.estado.${i.estado}`)} />
              <Linha rotulo={t('relogio.campos.origem')} valor={t(`relogio.origem.${i.origem}`)} />
              {i.erro && <Linha rotulo={t('relogio.campos.ultimoErro')} valor={traduzirErro(i.erro_codigo ?? undefined, i.erro)} />}
              {i.leiaute && <Linha rotulo={t('relogio.campos.leiaute')} valor={t(`relogio.leiaute.${i.leiaute}`)} />}
              {i.periodo_inicio && <Linha rotulo={t('relogio.campos.periodo')} valor={`${data(i.periodo_inicio)} - ${data(i.periodo_fim)}`} />}
              {i.nsr_inicial !== null && <Linha rotulo={t('relogio.campos.nsr')} valor={`${i.nsr_inicial} - ${i.nsr_final}`} />}
              {(
                [
                  ['lidas', i.lidas],
                  ['gravadas', i.gravadas],
                  ['existentes', i.existentes],
                  ['semFuncionario', i.sem_funcionario],
                  ['periodoFechado', i.periodo_fechado],
                  ['crcInvalido', i.crc_invalido],
                  ['invalidas', i.invalidas],
                  ['outros', i.outros_registros],
                  ['foraDeOrdem', i.fora_de_ordem],
                  ['lacunas', i.lacunas],
                ] as const
              ).map(([k, v]) => (
                <Linha key={k} rotulo={t(`relogio.campos.${k}`)} valor={numero(v)} />
              ))}
              <Linha rotulo={t('relogio.campos.pedidoPor')} valor={`${nomePessoa(i.pedido_por_nome) || '-'} · ${dataHora(i.created_at)}`} />
              {i.concluida_em && <Linha rotulo={t('relogio.campos.concluidaEm')} valor={dataHora(i.concluida_em)} />}
            </dl>
            <a href={`/api/v1/anexos/${i.anexo_id}`} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-acento-forte hover:underline" download>
              <Download className="size-4" aria-hidden />
              {t('relogio.baixarArquivo')}
            </a>
            {!!i.recusas?.length && (
              <>
                <h3 className="mb-2 text-sm font-semibold text-tinta-1">{t('relogio.campos.recusas')}</h3>
                <ul className="flex flex-col gap-2 text-sm">
                  {i.recusas.map((x) => (
                    <li key={`${x.linha}-${x.codigo}`} className="rounded-lg border border-borda p-3">
                      <span className="numero text-tinta-3">{`${t('relogio.campos.linha')} ${x.linha}${x.nsr !== null ? ` · NSR ${x.nsr}` : ''}`}</span>
                      <p className="text-tinta-1">{t(`relogio.recusa.${x.codigo}`, { defaultValue: x.motivo })}</p>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </Estado>
    </Drawer>
  );
}
